import { logger } from '../lib/utils';
import { useState, useCallback, useEffect } from 'react';

const TAP_MODE_KEY = 'jigsaw_tap_mode';

/**
 * Hook to manage Tap Mode for piece movement
 * In Tap Mode, players tap to select a piece, then tap to place it
 */
export function useTapMode() {
  // Load saved preference
  const [useTapMode, setUseTapMode] = useState(() => {
    try {
      const saved = localStorage.getItem(TAP_MODE_KEY);
      return saved === 'true';
    } catch {
      return false;
    }
  });
  
  // Selected piece state
  const [selectedPiece, setSelectedPiece] = useState(null);
  
  // Toggle tap mode and save preference
  const toggleTapMode = useCallback((enabled) => {
    setUseTapMode(enabled);
    setSelectedPiece(null); // Clear selection when switching modes
    try {
      localStorage.setItem(TAP_MODE_KEY, String(enabled));
    } catch (e) {
      logger.error('Failed to save tap mode preference:', e);
    }
  }, []);
  
  // Select a piece (from tray or board)
  const selectPiece = useCallback((piece, fromTray, sourceRow = null, sourceCol = null) => {
    // If tapping the same piece, deselect it
    if (selectedPiece && selectedPiece.piece.id === piece.id) {
      setSelectedPiece(null);
      return { action: 'deselect' };
    }
    
    setSelectedPiece({
      piece,
      fromTray,
      sourceRow,
      sourceCol
    });
    return { action: 'select' };
  }, [selectedPiece]);
  
  // Clear selection
  const clearSelection = useCallback(() => {
    setSelectedPiece(null);
  }, []);
  
  // Handle tap on a board cell (for placing or swapping)
  const handleBoardCellTap = useCallback((row, col, hasPiece, targetPieceCorrect, onPlace, onSwap) => {
    if (!selectedPiece) {
      return { action: 'none', reason: 'no_selection' };
    }
    
    const { piece, fromTray, sourceRow, sourceCol } = selectedPiece;
    
    // If tapping on the same cell where the piece came from, deselect
    if (!fromTray && sourceRow === row && sourceCol === col) {
      setSelectedPiece(null);
      return { action: 'deselect' };
    }
    
    if (hasPiece && !targetPieceCorrect) {
      // Swap pieces
      onSwap(piece.id, sourceRow, sourceCol, row, col, fromTray);
      setSelectedPiece(null);
      return { action: 'swap' };
    } else if (!hasPiece) {
      // Place piece
      onPlace(piece.id, row, col, fromTray, sourceRow, sourceCol);
      setSelectedPiece(null);
      return { action: 'place' };
    }
    
    // Can't place on a correctly placed piece
    return { action: 'none', reason: 'cell_occupied_correct' };
  }, [selectedPiece]);
  
  // Handle tap on tray (for returning piece from board)
  const handleTrayTap = useCallback((onReturnToTray) => {
    if (!selectedPiece) {
      return { action: 'none', reason: 'no_selection' };
    }
    
    const { fromTray, sourceRow, sourceCol } = selectedPiece;
    
    // Can only return pieces that are on the board
    if (fromTray) {
      setSelectedPiece(null);
      return { action: 'deselect', reason: 'piece_already_in_tray' };
    }
    
    // Return piece to tray
    onReturnToTray(sourceRow, sourceCol);
    setSelectedPiece(null);
    return { action: 'return_to_tray' };
  }, [selectedPiece]);
  
  // Check if a piece is currently selected
  const isPieceSelected = useCallback((pieceId) => {
    return selectedPiece?.piece?.id === pieceId;
  }, [selectedPiece]);
  
  return {
    useTapMode,
    toggleTapMode,
    selectedPiece,
    selectPiece,
    clearSelection,
    handleBoardCellTap,
    handleTrayTap,
    isPieceSelected
  };
}
